import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { ScheduleType } from 'webapp/enums/enums';
import { getDeliveryTimeLevel } from 'webapp/utils/number';

import Serviced from 'webapp/models-custom/serviced';

// wrapper for read-only getters that repeat across the models (Scorecard, BoardShare, ReportSchedule)
// the props still have to be set on the models themselves, along with these
export interface ScheduleUser {
	id: number;
	timezone: string;
}

type ThisProp = 'type' | 'hourOfDay' | 'dayOfWeek' | 'dayOfMonth';
interface PropertyMap {
	type: string;
	hourOfDay: string;
	dayOfWeek: string;
	dayOfMonth: string;
	user: string;
}

export default class Schedule extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;

	@tracked type: number;
	@tracked hourOfDay: number | null;
	@tracked dayOfWeek: number[];
	@tracked dayOfMonth: number | null;
	user: ScheduleUser | null;

	propertyMap: PropertyMap;

	constructor(
		type: number = ScheduleType.ONE_OFF,
		hourOfDay: number | null = null,
		dayOfWeek: number[] = [],
		dayOfMonth: number | null = null,
		user: ScheduleUser | null = null,
		propertyMap: PropertyMap,
	) {
		super();

		this.type = type;
		this.hourOfDay = hourOfDay;
		this.dayOfWeek = dayOfWeek;
		this.dayOfMonth = dayOfMonth;
		this.user = user;

		this.propertyMap = propertyMap;
	}

	get isOneOff(): boolean {
		return this.type === ScheduleType.ONE_OFF;
	}

	get isDaily(): boolean {
		return this.type === ScheduleType.DAILY;
	}

	get isWeekly(): boolean {
		return this.type === ScheduleType.WEEKLY;
	}

	get isMonthly(): boolean {
		return this.type === ScheduleType.MONTHLY;
	}

	get isQuarterly(): boolean {
		return this.type === ScheduleType.QUARTERLY;
	}

	get hasDayOfWeek(): boolean {
		return this.isWeekly;
	}

	get hasDayOfMonth(): boolean {
		return this.isMonthly || this.isQuarterly;
	}

	get hasHourOfDay(): boolean {
		return this.isDaily || this.isWeekly || this.isMonthly || this.isQuarterly;
	}

	get typeConstant(): LabelValuePairNumeric {
		return this.constantsService.scheduleTypes.find((item) => item.value === this.type)!;
	}

	get typeText(): string {
		if (this.isOneOff) {
			return 'One-off';
		} else {
			return this.typeConstant?.label || '';
		}
	}

	get timeText(): string {
		const constantsService = this.constantsService;
		let scheduleText = '',
			tmp;

		// day
		if (this.hasDayOfWeek) {
			const days: string[] = [];
			const firstDay = this.accountService.firstDayOfWeek % 7;
			const sortedDays = [...this.dayOfWeek].sort(
				(a, b) => ((a - firstDay + 7) % 7) - ((b - firstDay + 7) % 7),
			);

			sortedDays.forEach((day) => {
				const dayOfWeek = constantsService.daysOfWeek.find((item) => item.value === day);

				if (dayOfWeek) {
					days.push(dayOfWeek.label);
				}
			});

			tmp = { label: days.join(', ') };
		}
		if (this.hasDayOfMonth) {
			tmp = this.valuesForDayOfMonth.find((item) => item.value === this.dayOfMonth!);
		}
		if (tmp) {
			scheduleText += `every ${tmp.label}`;
		}

		// time
		if (this.hasHourOfDay) {
			tmp = constantsService.hoursOfDay.find((item) => item.value === this.hourOfDay!);
			if (tmp) {
				scheduleText += ` at ${tmp.label}`;
			}
		}

		return scheduleText;
	}

	get valuesForDayOfMonth(): LabelValuePairNumeric[] {
		return this.isQuarterly
			? this.constantsService.daysOfMonthFilteredForQuarterly
			: this.constantsService.daysOfMonthFiltered;
	}

	get deliveryTimeLevel(): number {
		const { isWeekly, isMonthly, isQuarterly, hourOfDay, dayOfWeek, dayOfMonth } = this;

		return getDeliveryTimeLevel({
			hourOfDay,
			dayOfWeek,
			dayOfMonth,
			isWeekly,
			isMonthly,
			isQuarterly,
		});
	}

	updateScheduleValues(): void {
		// called when frequency has changed and we need to validate / reset the visible fields

		if (this.hasHourOfDay && typeof this.hourOfDay !== 'number') {
			this.hourOfDay = 9;
		}

		if (
			this.hasDayOfMonth &&
			(typeof this.dayOfMonth !== 'number' ||
				!this.valuesForDayOfMonth.find((item) => item.value === this.dayOfMonth))
		) {
			this.dayOfMonth = this.isQuarterly ? -1 : 1;
		}

		if (this.hasDayOfWeek && !this.dayOfWeek.length) {
			this.dayOfWeek = [2];
		}
	}

	cleanupUnusedScheduleValues(): void {
		// called before saving to cleanup the unused fields

		if (!this.hasDayOfWeek) {
			this.dayOfWeek = [];
		}

		if (!this.hasDayOfMonth) {
			this.dayOfMonth = null;
		}

		if (!this.hasHourOfDay) {
			this.hourOfDay = null;
		}
	}

	// the model has the schedule props under the property map's names (Reflect.get/set: same as model[modelProp])
	updateScheduleFromModel(model: object): void {
		const propertyMap = this.propertyMap;

		const updateProp = <K extends ThisProp>(thisProp: K) => {
			const value = Reflect.get(model, propertyMap[thisProp]) as this[K];
			if (value !== this[thisProp]) {
				this[thisProp] = value;
			}
		};
		(Object.keys(propertyMap) as ThisProp[]).forEach(updateProp);
	}

	updateModelFromSchedule(model: object): void {
		const propertyMap = this.propertyMap;

		Object.keys(propertyMap).forEach((key) => {
			const thisProp = key as ThisProp;
			const modelProp = propertyMap[thisProp];
			if (Reflect.get(model, modelProp) !== this[thisProp]) {
				Reflect.set(model, modelProp, this[thisProp]);
			}
		});
	}

	static createFromModel(model: object, propertyMap?: PropertyMap): Schedule {
		if (!propertyMap) {
			propertyMap = {
				type: 'scheduleType',
				hourOfDay: 'scheduleHourOfDay',
				dayOfWeek: 'scheduleDayOfWeek',
				dayOfMonth: 'scheduleDayOfMonth',
				user: 'scheduleUser',
			};
		}
		return new Schedule(
			Reflect.get(model, propertyMap.type) as number,
			Reflect.get(model, propertyMap.hourOfDay) as number | null,
			Reflect.get(model, propertyMap.dayOfWeek) as number[],
			Reflect.get(model, propertyMap.dayOfMonth) as number | null,
			Reflect.get(model, propertyMap.user) as ScheduleUser | null,
			propertyMap,
		);
	}
}
